import React from "react";
import { Link } from "react-router-dom";
import { Facebook, Instagram, Youtube } from "lucide-react";
import Logo from "@/components/Logo";
import { STATES, STATE_RESOURCES } from "@/lib/states";

// Social media destinations. Centralised so we only update them once.
const SOCIALS = [
  { name: "Facebook group", href: "https://www.facebook.com/groups/tenanttrail", Icon: Facebook },
  { name: "Instagram", href: "https://www.instagram.com/tenantrail/", Icon: Instagram },
  { name: "YouTube", href: "https://www.youtube.com/@TenantTrail", Icon: Youtube },
];

/**
 * SiteFooter, legal & compliance footer for every public page.
 * Intentionally minimal: disclaimer, privacy, terms, and the state
 * tenant services that back up our "not legal advice" stance.
 * Product navigation lives in the top nav, not here.
 */
export default function SiteFooter() {
  const year = new Date().getFullYear();
  return (
    <footer className="border-t border-[#E5E4DE] bg-[#FAF9F6]" data-testid="site-footer">
      <div className="max-w-4xl mx-auto px-6 sm:px-8 lg:px-12 py-12">

        {/* Brand mark + socials */}
        <div className="flex items-center justify-between gap-3 pb-8 border-b border-[#E5E4DE]">
          <div className="flex items-center gap-3">
            <Logo size={28} />
            <div>
              <div className="font-serif text-[#0D2B52] text-base">TenantTrail</div>
              <div className="text-xs text-[#6B7280]">Made in Australia, for Australian renters.</div>
            </div>
          </div>
          <div className="flex items-center gap-1.5" data-testid="footer-socials">
            {SOCIALS.map(({ name, href, Icon }) => (
              <a
                key={name}
                href={href}
                target="_blank"
                rel="noopener noreferrer"
                aria-label={name}
                title={name}
                className="p-2 text-[#6B7280] hover:text-[#1B6C62] hover:bg-[#E6F5F2] transition-colors"
                data-testid={`footer-social-${name.toLowerCase().split(' ')[0]}`}
              >
                <Icon size={16} strokeWidth={1.75} />
              </a>
            ))}
          </div>
        </div>

        {/* Legal disclaimer */}
        <div className="pt-8 pb-6 text-[12.5px] text-[#5a6b7a] leading-relaxed font-serif" data-testid="footer-disclaimer">
          <p className="mb-3">
            <strong className="text-[#0D2B52]">TenantTrail is not a law firm and does not provide legal advice.</strong>{" "}
            Compass and the Tenant Brain provide general legal information about Australian tenancy law to help you understand your rights, responsibilities and options. They are not a substitute for advice from a qualified solicitor or your state&rsquo;s free Tenants&rsquo; Union. If your matter is serious, time-critical, or involves a court or tribunal, please contact one of the free services below for your state. See our{" "}
            <Link to="/disclaimer" className="underline hover:text-[#0D2B52]" data-testid="footer-disclaimer-inline">Legal Disclaimer</Link> for the full picture.
          </p>
          <p className="mb-0">
            TenantTrail complies with the Australian <em>Privacy Act 1988</em> and the Australian Consumer Law. Your documents are stored on infrastructure located in Australia and never sold, shared, or used to train public AI models. See our{" "}
            <Link to="/privacy" className="underline hover:text-[#0D2B52]">Privacy Policy</Link> for the full picture.
          </p>
        </div>

        {/* State-by-state tenant resources (supports the disclaimer above) */}
        <details className="pt-6 border-t border-[#E5E4DE] group" data-testid="footer-state-resources">
          <summary className="cursor-pointer list-none flex items-center justify-between label-mono text-[10px] text-[#0D2B52] hover:text-[#1B6C62]">
            <span>Free tenant services, by state &amp; territory</span>
            <span className="text-[11px] font-mono text-[#6B7280] group-open:rotate-180 transition-transform">▾</span>
          </summary>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-x-6 gap-y-5 mt-4">
            {STATES.map((s) => (
              <div key={s.code} data-testid={`footer-state-${s.code}`}>
                <div className="font-serif text-[13px] text-[#0D2B52] mb-1">{s.name} <span className="font-mono text-[10px] text-[#6B7280]">({s.code})</span></div>
                <ul className="space-y-0.5 text-[11.5px] text-[#5a6b7a]">
                  {(STATE_RESOURCES[s.code] || []).map(([name, url]) => (
                    <li key={url}>
                      <a href={url} target="_blank" rel="noopener noreferrer" className="hover:text-[#1B6C62] hover:underline">
                        {name}
                      </a>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </details>

        {/* Bottom bar: grouped links, then copyright + tagline on one line */}
        <div className="mt-8 pt-6 border-t border-[#E5E4DE]" data-testid="footer-bottom-bar">
          <div className="grid grid-cols-[auto_1fr] gap-x-5 gap-y-2 text-[11.5px] text-[#5a6b7a] font-serif mb-6">
            <span className="font-mono text-[9.5px] uppercase tracking-[0.14em] text-[#0D2B52] pt-[3px]">Explore</span>
            <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
              <a href="/api/agent-tricks" className="hover:text-[#0D2B52]" data-testid="footer-agent-tricks">Agent tricks</a>
              <span className="text-[#D4CFC5]">&middot;</span>
              <Link to="/check-a-deduction" className="hover:text-[#0D2B52]" data-testid="footer-check-deduction">Check a deduction</Link>
              <span className="text-[#D4CFC5]">&middot;</span>
              <Link to="/trust" className="hover:text-[#0D2B52]" data-testid="footer-trust">Trust and security</Link>
              <span className="text-[#D4CFC5]">&middot;</span>
              <Link to="/founder" className="hover:text-[#0D2B52]" data-testid="footer-founder">About the founder</Link>
            </div>

            <span className="font-mono text-[9.5px] uppercase tracking-[0.14em] text-[#0D2B52] pt-[3px]">Legal</span>
            <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
              <Link to="/privacy" className="hover:text-[#0D2B52]" data-testid="footer-privacy">Privacy</Link>
              <span className="text-[#D4CFC5]">&middot;</span>
              <Link to="/terms" className="hover:text-[#0D2B52]" data-testid="footer-terms">Terms</Link>
              <span className="text-[#D4CFC5]">&middot;</span>
              <Link to="/disclaimer" className="hover:text-[#0D2B52]" data-testid="footer-disclaimer-link">Disclaimer</Link>
              <span className="text-[#D4CFC5]">&middot;</span>
              <Link to="/refunds" className="hover:text-[#0D2B52]" data-testid="footer-refunds">Refunds</Link>
            </div>

            <span className="font-mono text-[9.5px] uppercase tracking-[0.14em] text-[#0D2B52] pt-[3px]">Help</span>
            <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
              <Link to="/support" className="hover:text-[#0D2B52]" data-testid="footer-support">Support</Link>
            </div>
          </div>

          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 text-[11px] text-[#6B7280] font-mono pt-4 border-t border-[#E5E4DE]">
            <div>&copy; {year} TenantTrail. All rights reserved.</div>
            <div className="italic font-serif text-[10.5px] text-[#9CA3AF]">
              Protection &middot; Evidence &middot; Accountability.
            </div>
          </div>
        </div>

      </div>
    </footer>
  );
}
